import { Injectable, NgZone } from '@angular/core';
import { Course } from '../interfaces/course';
import { Trainer } from '../interfaces/trainer';
import { WorkoutTip } from '../interfaces/workout-tip';

import mockDataJson from '../data/db.json';
import { BehaviorSubject, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { Community } from '../interfaces/community';
import { Training } from '../interfaces/training';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, filter, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ClientLogin } from '../interfaces/client';
import {
  CommitmentContract,
  CommitmentContractSummary,
} from '../interfaces/contract';
import { handlePromise } from '../utils/promises.util';
import { CreditsRankingPeriod, CreditsRankingResponse } from '../interfaces/credits-ranking';
import { GymService } from './gym.service';


@Injectable({
  providedIn: 'root',
})
export class DataService {
  mockData = mockDataJson;
  private readonly baseUrl = environment.apiUrl;
  _newCourses = null;
  _courses = null;
  _trainers = null;
  _events = null;

  private coursesRequest$?: Observable<any>;
  private coursesLoadedAt = 0;
  private eventsRequest$?: Observable<any>;
  private eventsLoadedAt = 0;
  private trainersRequest$?: Observable<any>;
  private trainersLoadedAt = 0;
  private trainersGymId = '';
  private nextClassesRequest$?: Observable<any>;
  private nextClassesLoadedAt = 0;
  private usersInMuscleRequest$?: Observable<any>;
  private usersInMuscleLoadedAt = 0;
  private entriesRequestByUserId = new Map<string, { at: number; req$: Observable<any> }>();
  private creditsRequestByUserId = new Map<string, { at: number; req$: Observable<any> }>();
  private classesByModalityRequest = new Map<string, { at: number; req$: Observable<any> }>();
  private allClassesAndPassRequestByKey = new Map<string, { at: number; req$: Observable<any> }>();
  private waitingListByModalityRequest = new Map<string, { at: number; req$: Observable<any> }>();
  private userClassesRequestByUserId = new Map<string, { at: number; req$: Observable<any> }>();
  private userHistoricClassesRequestByUserId = new Map<string, { at: number; req$: Observable<any> }>();
  private userEventBookingsRequestByUserId = new Map<string, { at: number; req$: Observable<any> }>();
  private bookingsByClassRequest = new Map<string, { at: number; req$: Observable<any> }>();
  private creditsRankingRequestByPeriod = new Map<string, { at: number; req$: Observable<CreditsRankingResponse> }>();

  private userSubject = new BehaviorSubject<any>(null);
  user$ = this.userSubject.asObservable();

  private userClassesSubject = new BehaviorSubject<any[]>([]);
  userClasses$ = this.userClassesSubject.asObservable();

  private userHistoricClassesSubject = new BehaviorSubject<any[]>([]);
  userHistoricClasses$ = this.userHistoricClassesSubject.asObservable();

  private classesSubject = new BehaviorSubject<any[]>([]);
  classes$ = this.classesSubject.asObservable();

  private eventsSubject = new BehaviorSubject<any[]>([]);
  events$ = this.eventsSubject.asObservable();

  private classesAndPassSubject = new BehaviorSubject<any[]>([]);
  classesAndPass$ = this.classesAndPassSubject.asObservable();

  private usersInMuscleSubject = new BehaviorSubject<any[]>([]);
  usersInMuscle$ = this.usersInMuscleSubject.asObservable();

  private userAccessSubject = new BehaviorSubject<any>({});
  userAccess$ = this.userAccessSubject.asObservable();

  private usersInWaitingListSubject = new BehaviorSubject<any[]>([]);
  usersInWaitingList$ = this.usersInWaitingListSubject.asObservable();

  private contractJustSignedSubject = new BehaviorSubject<CommitmentContract | null>(null);
  contractJustSigned$ = this.contractJustSignedSubject.asObservable();

  setContractJustSigned(contract: CommitmentContract | null) {
    this.contractJustSignedSubject.next(contract);
  }

  getContractJustSignedSnapshot(): CommitmentContract | null {
    return this.contractJustSignedSubject.getValue();
  }

  consumeContractJustSigned(): CommitmentContract | null {
    const value = this.contractJustSignedSubject.getValue();
    if (value) this.contractJustSignedSubject.next(null);
    return value;
  }


  _classes = null;
  _allClasses = null;
  _allClassesAndPass = null;
  token = '';
  _credits = null;
  _tokenFCM = null;

  static hasTrainerName(instructor: any): boolean {
    if (!instructor) return false;
    if (typeof instructor === 'string') return instructor.trim().length > 0;
    if (typeof instructor.fullName === 'string' && instructor.fullName.trim().length > 0) return true;
    return [instructor.name, instructor.surname, instructor.lastName]
      .some((v: any) => typeof v === 'string' && v.trim().length > 0);
  }

  static formatTrainerName(obj: any, modalities?: any[]): string | null {
    if (!obj) return null;
    let trainer: any = obj?.trainer ?? obj?.instructor ?? obj?.modality?.trainer ?? obj?.modality?.instructor ?? null;

    if (!DataService.hasTrainerName(trainer) && Array.isArray(modalities) && modalities.length > 0 && obj?.modality?.id) {
      const cachedModality = modalities.find((m: any) => String(m.id) === String(obj.modality.id));
      const fallback = cachedModality?.instructor ?? cachedModality?.trainer ?? null;
      if (DataService.hasTrainerName(fallback)) trainer = fallback;
    }

    if (!trainer) return null;
    if (typeof trainer === 'string') {
      return trainer.trim().length > 0 ? trainer : null;
    }
    if (typeof trainer.fullName === 'string' && trainer.fullName.trim().length > 0) {
      return trainer.fullName.trim();
    }
    const nameParts = [trainer.name, trainer.surname, trainer.lastName]
      .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
    return nameParts.length > 0 ? nameParts.join(' ') : null;
  }

  static resolveInstructorPayload(obj: any, modalities?: any[]): any {
    if (!obj) return obj;
    const current = obj?.instructor ?? obj?.trainer ?? null;
    if (DataService.hasTrainerName(current)) return obj;
    const modalityInstructor = obj?.modality?.instructor ?? obj?.modality?.trainer ?? null;
    if (DataService.hasTrainerName(modalityInstructor)) {
      (obj as any).instructor = modalityInstructor;
      return obj;
    }
    if (Array.isArray(modalities) && modalities.length > 0 && obj?.modality?.id) {
      const cachedModality = modalities.find((m: any) => String(m.id) === String(obj.modality.id));
      const cachedInstructor = cachedModality?.instructor ?? cachedModality?.trainer ?? null;
      if (DataService.hasTrainerName(cachedInstructor)) {
        (obj as any).instructor = cachedInstructor;
      }
    }
    return obj;
  }

  private creditsSubject = new BehaviorSubject<any[]>([]);
  credits$ = this.creditsSubject.asObservable();
  private creditsLoadedSubject = new BehaviorSubject<boolean>(false);
  creditsLoaded$ = this.creditsLoadedSubject.asObservable();

  private availableBookingsSubject = new BehaviorSubject<any>(null);
  availableBookings$ = this.availableBookingsSubject.asObservable();
  private availableBookingsLoadedAt = 0;
  private availableBookingsRequest$?: Observable<any>;

  private routinesSubject = new BehaviorSubject<any[]>([]);
  routines$ = this.routinesSubject.asObservable();

  private exercisesRequest$?: Observable<any[]>;
  private exercisesLoadedAt = 0;
  private allClassesRequest$?: Observable<any>;
  private allClassesLoadedAt = 0;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private gymService: GymService,
  ) {
    const savedUser = this.getLocalStorage('user');
    const token = this.getToken();
    if (token && !this.isTokenExpired(token) && savedUser) {
      this.ngZone.run(() => this.userSubject.next(savedUser));
      this.getCredits(savedUser.id).subscribe({ next: (data) => this.updateCredistUser(data), error: () => { } });
      this.getAvailableBookings(savedUser.id).subscribe({ next: () => { }, error: () => { } });
      this.getClassesToClient().subscribe({ next: () => { }, error: () => { } });
      this.getHistoricClassesToClient().subscribe({ next: () => { }, error: () => { } });
    } else {
      this.logout();
    }
  }

  private normalizeContractSummary(contract: any): CommitmentContractSummary {
    const signedAtRaw =
      contract?.signedAt ??
      contract?.signatureMetadata?.signedAt ??
      contract?.signatureMetadata?.acceptedAt ??
      null;
    return {
      id: contract?.id ?? contract?.contractId ?? '',
      status: contract?.status ?? 'CREATED',
      documentUrl: contract?.documentUrl ?? null,
      contractVersion: contract?.contractVersion ?? null,
      subscriptionId: contract?.subscriptionId ?? null,
      clientRateId: contract?.clientRateId ?? null,
      customerId: contract?.customerId ?? null,
      signedAt: signedAtRaw,
      createdAt: contract?.createdAt ?? null,
      commitmentStartAt: contract?.commitmentStartAt ?? null,
      commitmentEndAt: contract?.commitmentEndAt ?? null,
      commitmentMonths: contract?.commitmentMonths ?? null,
    } as any;
  }

  private normalizeContract(contract: any): CommitmentContract {
    const summary = this.normalizeContractSummary(contract) as any;
    const commitmentStartAtRaw =
      contract?.commitmentStartAt ??
      summary.commitmentStartAt ??
      contract?.signatureMetadata?.signedAt ??
      contract?.signedAt ??
      null;
    const commitmentMonthsRaw =
      contract?.commitmentMonths ?? summary.commitmentMonths ?? 12;
    const computedEnd = (() => {
      if (contract?.commitmentEndAt || summary.commitmentEndAt) {
        return contract?.commitmentEndAt ?? summary.commitmentEndAt;
      }
      if (!commitmentStartAtRaw) return null;
      try {
        const d = new Date(commitmentStartAtRaw);
        if (Number.isNaN(d.getTime())) return null;
        d.setMonth(d.getMonth() + Number(commitmentMonthsRaw || 12));
        return d.toISOString();
      } catch {
        return null;
      }
    })();

    return {
      ...summary,
      documentHash: contract?.documentHash ?? null,
      signatureMetadata: contract?.signatureMetadata ?? null,
      breachPaymentMetadata: contract?.breachPaymentMetadata ?? [],
      planOption: contract?.planOption ?? null,
      commitmentMonths: commitmentMonthsRaw,
      commitmentStartAt: commitmentStartAtRaw,
      commitmentEndAt: computedEnd,
      document: contract?.document
        ? {
            content: contract.document.content ?? '',
            mimeType: contract.document.mimeType ?? null,
            createdAt: contract.document.createdAt ?? null,
          }
        : null,
    };
  }

  getProfile() {
    return this.getUser();
  }

  getContractsBySubscription(subscriptionId: string): Observable<CommitmentContractSummary[]> {
    if (!subscriptionId) {
      return of([]);
    }

    return this.http
      .get<any[]>(`${this.baseUrl}/api/contracts?subscriptionId=${subscriptionId}`)
      .pipe(
        map((response: any[]) =>
          Array.isArray(response)
            ? response.map((contract) => this.normalizeContractSummary(contract))
            : [],
        ),
        catchError((error) => {
          console.error(error);
          throw error;
        }),
      );
  }

  getLatestContractBySubscription(
    subscriptionId: string,
  ): Observable<CommitmentContractSummary | null> {
    return this.getContractsBySubscription(subscriptionId).pipe(
      map((contracts) => {
        if (!contracts.length) {
          return null;
        }

        const signedContract =
          contracts.find((contract) => contract.status === 'SIGNED') ?? null;

        return signedContract ?? contracts[0];
      }),
    );
  }

  getContract(contractId: string): Observable<CommitmentContract> {
    return this.http.get(`${this.baseUrl}/api/contracts/${contractId}`).pipe(
      map((response: any) => this.normalizeContract(response)),
      catchError((error) => {
        console.error(error);
        throw error;
      }),
    );
  }

  signContract(
    contractId: string,
    body: {
      signatureAccepted: boolean;
      actorId?: string;
      actorType?: string;
    },
  ): Observable<CommitmentContract> {
    return this.http
      .post(`${this.baseUrl}/api/contracts/${contractId}/sign`, body)
      .pipe(
        map((response: any) => this.normalizeContract(response)),
        catchError((error) => {
          console.error(error);
          throw error;
        }),
      );
  }

  continueCommitmentPayment(
    contractId: string,
    clientId: string,
  ): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/api/credits`, {
        amount: 28,
        option: 6,
        contractId,
        clients: [{ id: clientId }],
      })
      .pipe(
        map((response: any) => response),
        catchError((error) => {
          console.error(error);
          throw error;
        }),
      );
  }

  /**
   * Contratos de permanencia ligados a una tarifa plana (nuevo flujo),
   * análogos a los ligados a créditos legacy.
   */
  getContractsByClientRate(
    clientRateId: string,
  ): Observable<CommitmentContractSummary[]> {
    if (!clientRateId) {
      return of([]);
    }

    return this.http
      .get<any[]>(
        `${this.baseUrl}/api/contracts?clientRateId=${clientRateId}`,
      )
      .pipe(
        map((response: any[]) =>
          Array.isArray(response)
            ? response.map((contract) => this.normalizeContractSummary(contract))
            : [],
        ),
        catchError((error) => {
          console.error(error);
          throw error;
        }),
      );
  }

  getLatestContractByClientRate(
    clientRateId: string,
  ): Observable<CommitmentContractSummary | null> {
    return this.getContractsByClientRate(clientRateId).pipe(
      map((contracts) => {
        if (!contracts.length) {
          return null;
        }

        const signedContract =
          contracts.find((contract) => contract.status === 'SIGNED') ?? null;

        return signedContract ?? contracts[0];
      }),
    );
  }

  /**
   * Continúa el pago de una tarifa plana con suscripción (equivale a
   * `continueCommitmentPayment` en el flujo legacy de musculación anual).
   */
  continueClientRatePayment(
    clientRateId: string,
    clientId: string,
  ): Observable<any> {
    const headers = new HttpHeaders().set('x-client-id', clientId);
    return this.http
      .post(
        `${this.baseUrl}/api/client-rates/${clientRateId}/continue-payment`,
        {},
        { headers },
      )
      .pipe(
        map((response: any) => response),
        catchError((error) => {
          console.error(error);
          throw error;
        }),
      );
  }

  getNewCourses(): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/modalities`).pipe(
      map(response => {
        this.newCourses = response;
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  getUsersInMuscle(): Observable<any> {
    const now = Date.now();
    if (this.usersInMuscleRequest$ && now - this.usersInMuscleLoadedAt < 15000) {
      return this.usersInMuscleRequest$;
    }
    this.usersInMuscleLoadedAt = now;
    this.usersInMuscleRequest$ = this.http.get(`${this.baseUrl}/api/access/users`).pipe(
      map(response => {
        this.updateUserMuscle(response);
        return response;
      }),
      catchError(error => {
        this.usersInMuscleRequest$ = undefined;
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    return this.usersInMuscleRequest$;
  }

  getEntries(): Observable<any> {
    const user = this.getUser();
    if (!user?.id) {
      return of([]);
    }
    const now = Date.now();
    const cached = this.entriesRequestByUserId.get(user.id);
    if (cached && now - cached.at < 15000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/access/user/${user.id}`).pipe(
      map(response => {
        this.updateUserAccess(response);
        return response;
      }),
      catchError(error => {
        this.entriesRequestByUserId.delete(user.id);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.entriesRequestByUserId.set(user.id, { at: now, req$ });
    return req$;
  }

  getCourses(forceRefresh: boolean = false): Observable<any> {
    // Si forzamos refresco: invalidamos memoria y localStorage para recoger cambios
    // (modalidades nuevas o nombres editados en el admin).
    if (forceRefresh) {
      this.courses = null;
      this._courses = null;
      try {
        localStorage.removeItem('courses');
      } catch {}
      this.coursesRequest$ = undefined;
      this.coursesLoadedAt = 0;
    }

    const stored = this.getLocalStorage('courses');
    if (!forceRefresh && !this.courses && stored) {
      this.courses = stored;
    }
    const now = Date.now();
    const hasFreshRequest =
      !!this.coursesRequest$ && now - this.coursesLoadedAt < 5 * 60 * 1000;

    // Devolvemos lo cacheado EN PARALELO a disparar una petición real (stale-while-revalidate).
    // La UI pinta rápido, y cuando llega la API nueva sobreescribe courses/localStorage.
    // Solo si NO hay request en curso para no hacer doble petición simultánea.
    if (this.courses && Array.isArray(this.courses) && this.courses.length > 0) {
      if (!hasFreshRequest && !forceRefresh) {
        this.refreshCoursesInBackground();
      }
      return of(this.courses);
    }

    if (hasFreshRequest) {
      return this.coursesRequest$;
    }
    this.coursesLoadedAt = now;
    this.coursesRequest$ = this.fetchCoursesOnce();
    return this.coursesRequest$;
  }

  private refreshCoursesInBackground(): void {
    // Dispara una petición real sin cache para refrescar silenciosamente el listado.
    // Si falla no afecta a la UI (ya tenemos los datos antiguos).
    try {
      this.fetchCoursesOnce()
        .pipe(take(1))
        .subscribe({
          next: () => {},
          error: () => {},
        });
    } catch {}
  }

  private fetchCoursesOnce(): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/api/modalities`).pipe(
      map((response: any[]) => {
        const mapped = (response || []).map((m: any) => ({
          id: m.id ?? m._id,
          name: m.name,
          numMaxInscritos: m.numMaxInscritos ?? 0,
          numCreditos: m.numCreditos ?? 0,
          info: m.info ?? '',
          waitingList: m.waitingList ?? false,
          instructor: m.instructor,
          image: m.image ?? 'https://i.ibb.co/HB3MG86/logo-vertical.jpg',
          time: m.time ?? 60,
          intensity: m.intensity ?? 'Media'
        }));
        this.setLocalStorage('courses', mapped);
        this.courses = mapped;
        this.newCourses = mapped;
        return mapped;
      }),
      catchError(error => {
        this.coursesRequest$ = undefined;
        console.error(error);
        throw error;
      }),
      finalize(() => {
        // Cuando la request termina, el TTL de 5 min ya lo marca coursesLoadedAt al hacer
        // la proxima llamada (más de 5 min se vuelve a lanzar freshRequest).
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
  }

  getAllEvents(): Observable<any> {
    const now = Date.now();
    if (this.events && Array.isArray(this.events) && this.events.length > 0 && now - this.eventsLoadedAt < 60 * 1000) {
      return of(this.events);
    }
    if (this.eventsRequest$ && now - this.eventsLoadedAt < 60 * 1000) {
      return this.eventsRequest$;
    }
    this.eventsLoadedAt = now;
    this.eventsRequest$ = this.http.get(`${this.baseUrl}/api/classes/events?includeBookings=0`).pipe(
      map((response: any) => {
        this.setLocalStorage('events', response);
        this.events = response;
        this.updateEvents(response);
        return response;
      }),
      catchError(error => {
        this.eventsRequest$ = undefined;
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    return this.eventsRequest$;
  }

  /*getCoursesByType(type: string): Course[] {
    return this.mockData.courses.filter((item) => item.type === type);
  }*/

  getTrainers(): Observable<any> {
    /*const trainers = this.getLocalStorage('trainers');
    if (trainers) {
      this.trainers = trainers;
      return trainers;
    }*/
    const now = Date.now();
    const gymId = this.gymService.getGym()?.id ?? '';
    const cacheValid = now - this.trainersLoadedAt < 10 * 60 * 1000;
    const gymMatches = gymId === this.trainersGymId;

    if (cacheValid && gymMatches) {
      if (this.trainers && Array.isArray(this.trainers) && this.trainers.length > 0) {
        return of(this.trainers);
      }
      if (this.trainersRequest$) {
        return this.trainersRequest$;
      }
    }

    this.trainersGymId = gymId;
    this.trainersLoadedAt = now;
    this.trainersRequest$ = this.http.get(`${this.baseUrl}/api/auth/instructors`).pipe(
      map((response: any) => {
        this.trainers = response.filter(t => t.roles.includes('instructor'));
        this.setLocalStorage('trainers', this.trainers);
        return response;
      }),
      catchError(error => {
        this.trainersRequest$ = undefined;
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    return this.trainersRequest$;
  }

  login(client: ClientLogin) {
    return this.http.post(`${this.baseUrl}/api/auth-client/login`, client).pipe(
      map((response: any) => {
        this.token = response.token;
        this.setLocalStorage('jwt', response.token);
        if (!response.user.tokenFCM) {
          this.saveToken(response.user.id, this._tokenFCM);
        }
        response.user.tokenFCM = this._tokenFCM;
        this.updateUser(response.user);
        this.setLocalStorage('user', response.user);
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  // Métodos biométricos se implementan en SigninPage usando plugins nativos

  getUserData(id: string) {

    this.getCredits(id).subscribe(c => {
      const creditActual = this.credits;
      const u: any = this.getUser();
      if (u && creditActual) {
        u.totalCredits = creditActual.amount - creditActual.spenders;
        this.updateUser(u);
      }
    });
  }

  getCommunity(): Community[] {
    return this.mockData.community;
  }

  getWorkoutTips(): WorkoutTip[] {
    return this.mockData.tips;
  }

  getTraining(): Training[] {
    return this.mockData.training;
  }

  getTrainingByDay(day: string): Training {
    return this.mockData.training.find((item) => item.weekday === day);
  }

  // Ejercicios
  getExercises(): Observable<any[]> {
    const now = Date.now();
    if (this.exercisesRequest$ && now - this.exercisesLoadedAt < 5 * 60 * 1000) {
      return this.exercisesRequest$;
    }
    this.exercisesLoadedAt = now;
    this.exercisesRequest$ = this.http.get<any[]>(`${this.baseUrl}/api/exercises`).pipe(
      map((response: any[]) => Array.isArray(response) ? response : []),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 5 * 60 * 1000 }),
      catchError(error => {
        this.exercisesRequest$ = undefined;
        this.exercisesLoadedAt = 0;
        console.error(error);
        throw error;
      })
    );
    return this.exercisesRequest$;
  }

  getExercisesByGroup(group: string): Observable<any[]> {
    return this.getExercises().pipe(
      map(items => items.filter((e: any) => String(e?.muscularGroup) === String(group)))
    );
  }

  saveExerciseLogs(exerciseId: string, entries: Array<{ reps: number; weight: number }>) {
    const user = this.getUser();
    if (!user?.id) {
      return throwError(() => new Error('No client id available'));
    }
    const payload = {
      clientId: user.id,
      exerciseId,
      entries: (entries || []).map((e, i) => ({ roundNumber: i + 1, reps: Number(e?.reps) || 0, weight: Number(e?.weight) || 0 }))
    };
    return this.http.put(`${this.baseUrl}/api/exercises/logs`, payload).pipe(
      map((response: any) => response),
      catchError(error => { console.error(error); throw error; })
    );
  }

  getExerciseLogs(exerciseId: string): Observable<Array<{ roundNumber: number; reps: number; weight: number }>> {
    const user = this.getUser();
    if (!user?.id) {
      return throwError(() => new Error('No client id available'));
    }
    const url = `${this.baseUrl}/api/exercises/${exerciseId}/logs?clientId=${user.id}`;
    return this.http.get<any[]>(url).pipe(
      map((items: any[]) => Array.isArray(items) ? items : []),
      catchError(error => { console.error(error); throw error; })
    );
  }

  getExerciseLogsByGroup(group: string): Observable<any[]> {
    const user = this.getUser();
    if (!user?.id) {
      return throwError(() => new Error('No client id available'));
    }
    const url = `${this.baseUrl}/api/exercises/logs/by-group?clientId=${user.id}&group=${encodeURIComponent(group)}`;
    return this.http.get<any[]>(url).pipe(
      map((items: any[]) => Array.isArray(items) ? items : []),
      catchError(error => { console.error(error); throw error; })
    );
  }

  getClasesByModality(modalityId: string, includeBookings = true) {
    const key = `${modalityId}:${includeBookings ? 1 : 0}`;
    const now = Date.now();
    const cached = this.classesByModalityRequest.get(key);
    if (cached && now - cached.at < 30 * 1000) {
      return cached.req$;
    }
    const url = `${this.baseUrl}/api/classes/modalities/${modalityId}?includeBookings=${includeBookings ? 1 : 0}`;
    const req$ = this.http.get(url).pipe(
      map((response: any) => {
        this.classes = response;
        this.updateClasses(response);
        return response;
      }),
      catchError(error => {
        this.classesByModalityRequest.delete(key);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.classesByModalityRequest.set(key, { at: now, req$ });
    return req$;
  }

  getAllClases() {
    const now = Date.now();
    if (this.allClassesRequest$ && now - this.allClassesLoadedAt < 30 * 1000) {
      return this.allClassesRequest$;
    }
    this.allClassesLoadedAt = now;
    this.allClassesRequest$ = this.http.get(`${this.baseUrl}/api/classes/`).pipe(
      map((response: any) => {
        this.updateClasses(response);
        return response;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 30 * 1000 }),
      catchError(error => {
        this.allClassesRequest$ = undefined;
        this.allClassesLoadedAt = 0;
        console.error(error);
        throw error;
      })
    );
    return this.allClassesRequest$;
  }

  getNextClasses(includeBookings = false) {
    const user = this.getUser();
    if (!user?.id) {
      return of([]);
    }
    const now = Date.now();
    if (this.nextClassesRequest$ && now - this.nextClassesLoadedAt < 30 * 1000) {
      return this.nextClassesRequest$;
    }
    this.nextClassesLoadedAt = now;
    this.nextClassesRequest$ = this.http
      .get(`${this.baseUrl}/api/classes/next?includeBookings=${includeBookings ? 1 : 0}`)
      .pipe(
        map((response: any) => response),
        catchError(error => {
          this.nextClassesRequest$ = undefined;
          console.error(error);
          throw error;
        }),
        shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
      );
    return this.nextClassesRequest$;
  }

  getAllClasesAndPass(includeBookings = true) {
    const key = includeBookings ? '1' : '0';
    const now = Date.now();
    const cached = this.allClassesAndPassRequestByKey.get(key);
    if (cached && now - cached.at < 30 * 1000) {
      return cached.req$;
    }
    const url = `${this.baseUrl}/api/classes/modalities/all?includeBookings=${includeBookings ? 1 : 0}`;
    const req$ = this.http.get(url).pipe(
      map((response: any) => {
        const modalitiesCached = this.getLocalStorage('courses');
        const arr = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];
        const merged = arr.map((cls: any) => DataService.resolveInstructorPayload(cls, modalitiesCached));
        this.allClassesAndPass = merged;
        return merged;
      }),
      catchError(error => {
        this.allClassesAndPassRequestByKey.delete(key);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.allClassesAndPassRequestByKey.set(key, { at: now, req$ });
    return req$;
  }

  getClassById(id: string, includeBookings = true) {
    const url = `${this.baseUrl}/api/classes/id/${id}?includeBookings=${includeBookings ? 1 : 0}`;
    return this.http.get(url).pipe(
      map((response: any) => response),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  registerClass(classId: string, credits: number) {
    const user = this.getUser();
    if (!user) {
      return throwError(() => new Error('No user'));
    }

    // Refrescar las tarifas del cliente antes de decidir el tipo de reserva.
    // Sin este refresco, si el snapshot está vacío (p.ej. tras un login nuevo)
    // la reserva cae en el flujo de créditos en lugar de usar la tarifa plana.
    return this.getAvailableBookings(user.id, true).pipe(
      switchMap(() => this.doRegisterClass(classId, credits)),
    );
  }

  private doRegisterClass(classId: string, credits: number) {
    const user = this.getUser();
    if (!user) {
      return throwError(() => new Error('No user'));
    }

    const targetClass = Array.isArray(this.getClasses())
      ? this.getClasses().find((c: any) => c?.id === classId)
      : null;
    const classModalityId =
      targetClass?.modality?.id ??
      targetClass?.modalityId ??
      targetClass?.modality_id ??
      null;
    const isMuscle = classModalityId === '12947e81-ef23-45be-b2fd-431d9a1f8664';

    if (isMuscle) {
      const onlyMuscleCredit = this.getActualCreditsOnlyMuscle();
      if (onlyMuscleCredit) {
        return throwError(() => new Error('MUSCLE_NO_BOOKING_REQUIRED'));
      }
    }

    const userClasses = this.getUserClasses();
    const existing = Array.isArray(userClasses)
      ? userClasses.find((c: any) =>
        c?.clase?.id === classId ||
        c?.class?.id === classId ||
        c?.claseId === classId ||
        c?.classId === classId,
      )
      : null;
    const existingModalityId =
      existing?.clase?.modality?.id ?? existing?.class?.modality?.id ?? null;
    const existingIsMuscle = existingModalityId === '12947e81-ef23-45be-b2fd-431d9a1f8664';
    if (existing) {
      if (!existingIsMuscle) {
        return throwError(() => new Error('ALREADY_REGISTERED'));
      }
      const accesses = this.getUserAccess();
      const now = Date.now();
      const ninetyMinutes = 90 * 60 * 1000;
      const lastEntry = Array.isArray(accesses)
        ? accesses
          .filter((e: any) => e?.client?.id === user?.id)
          .map((e: any) => {
            const d = e?.accessTime;
            const dt = d instanceof Date ? d : new Date(d);
            return Number.isFinite(dt.getTime()) ? dt.getTime() : 0;
          })
          .reduce((max: number, t: number) => (t > max ? t : max), 0)
        : 0;

      if (!lastEntry || now - lastEntry <= ninetyMinutes) {
        return throwError(() => new Error('ALREADY_REGISTERED'));
      }
    }

    const availableSnapshot = this.availableBookingsSubject.getValue();
    let selectedClientRateId: string | null = null;
    let finalCredits = credits;
    const today = this.getTodayDate();
    const clientRates = Array.isArray(availableSnapshot?.clientRates)
      ? availableSnapshot.clientRates
      : [];
    if (classModalityId && clientRates.length > 0) {
      // Se usa la PRIMERA tarifa plana vigente que incluya la modalidad de la
      // clase y tenga cupo en la VENTANA ACTUAL (el límite de reservas de las
      // tarifas planas se aplica por ventana, no global). Si ninguna la cubre,
      // la reserva no puede realizarse con tarifa plana.
      const picked = clientRates.find(
        (cr: any) =>
          this.toDateOnly(cr.expirationDate) >= today &&
          this.rateIncludesModality(cr, classModalityId) &&
          this.hasBookingsAvailableInCurrentWindow(cr, classModalityId),
      );

      if (!picked) {
        return throwError(() => new Error('NO_ACTIVE_RATE_FOR_MODALITY'));
      }

      selectedClientRateId = picked.id;
      finalCredits = 1;
    }

    const registerDto: any = {
      classId,
      clientId: user.id,
      credits: finalCredits,
    };
    if (selectedClientRateId) {
      registerDto.clientRateId = selectedClientRateId;
    }

    return this.http.post(`${this.baseUrl}/api/booking/create`, registerDto).pipe(
      map((response: any) => {
        const userClasses = this.getUserClasses();
        let updatedClasses: any = [];
        if (userClasses && userClasses.length > 0) {
          updatedClasses = [...this.getUserClasses(), response];
        } else {
          updatedClasses = [...updatedClasses, response];
        }
        this.updateUserClasses(updatedClasses);

        const usesClientRate = Boolean(response?.clientRateId ?? selectedClientRateId);

        if (!usesClientRate) {
          const hoy = new Date(response.clase.day);
          const actualCredits = typeof this.getActualCredits === 'function' ? this.getActualCredits() : null;
          const canCharge = hoy.getDay() !== 0 || (actualCredits && actualCredits.amount < 47);
          if (canCharge) {
            const debit = Number(response?.credits ?? response?.numCreditos ?? 0) || 0;
            user.totalCredits = Math.max(0, Number(user?.totalCredits || 0) - debit);
            this.updateUser(user);
          }
          this.getCredits(user.id).subscribe({ next: (d: any) => this.updateCredistUser(d), error: () => { } });
        }

        this.getAvailableBookings(user.id, true).subscribe({ next: () => { }, error: () => { } });
        return response;
      }),
      catchError(error => {
        console.error('Error en el registro de clase:', error);
        // Aquí puedes hacer un manejo más específico si lo necesitas
        return throwError(error);  // Propaga el error para que sea manejado en el componente
      })
    );
  }

  registerEvent(eventId: string, credits: number) {
    const user = this.getUser();
    if (!user) return null;

    const registerDto = { eventId, clientId: user.id, credits };
    return this.http.post(`${this.baseUrl}/api/booking/create`, registerDto).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        console.error('Error en el registro de evento:', error);
        // Aquí puedes hacer un manejo más específico si lo necesitas
        return throwError(error);  // Propaga el error para que sea manejado en el componente
      })
    );
  }

  registerInWaitingList(classId: string) {
    const user = this.getUser();
    if (!user) return null;
    const registerDto = { class: classId, client: user.id };
    return this.http.post(`${this.baseUrl}/api/waitinglist/create`, registerDto).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  unRegisterInWaitingList(bookingId: string) {
    return this.http.delete(`${this.baseUrl}/api/waitinglist/${bookingId}?clientId=${this.getUser().id}`).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  unRegisterClass(bookingId: string) {
    if (this.getUser()) {
      const user = this.getUser();

      return this.http.delete(`${this.baseUrl}/api/booking/${bookingId}?clientId=${user.id}`).pipe(
        map((response: any) => {
          const clases = this.getUserClasses();
          const classId = response?.clase?.id ?? response?.class?.id;
          if (Array.isArray(clases)) {
            const updatedClasses = [...clases.filter(p => (p.clase?.id ?? p.class?.id) !== classId)];
            this.updateUserClasses(updatedClasses);
          } else {
            if (!clases) {
              this.updateUserClasses([]);
            } else {
              this.updateUserClasses([clases]);
            }
          }

          const usesClientRate = Boolean(response?.clientRateId);
          if (!usesClientRate) {
            user.totalCredits = user.totalCredits + response.credits;
            this.updateUser(user);
          }

          this.getAvailableBookings(user.id, true).subscribe({ next: () => { }, error: () => { } });
          return response;
        }),
        catchError(error => {
          console.error(error);
          throw error;
        })
      );
    } else {
      return null;
    }
  }

  unRegisterEvent(bookingId: string, event?: boolean) {
    if (this.getUser()) {
      const registerDto = {
        bookingId,
        clientId: this.getUser().id
      }

      const url = !event ? `${this.baseUrl}/api/booking/${bookingId}?clientId=${this.getUser().id}` : `${this.baseUrl}/api/booking/${bookingId}?clientId=${this.getUser().id}&event=true`;
      return this.http.delete(url).pipe(
        map((response: any) => {
          return response;
        }),
        catchError(error => {
          console.error(error);
          throw error;
        })
      );
    } else {
      return null;
    }
  }

  updateBookingAttendance(bookingId: string, attends: boolean) {
    const body = { asiste: attends } as any;
    return this.http.patch(`${this.baseUrl}/api/booking/${bookingId}`, body).pipe(
      map((response: any) => response),
      catchError(error => { console.error(error); throw error; })
    );
  }

  getCredits(userId: string, force = false) {
    if (!userId) {
      return of([]);
    }
    const now = Date.now();
    if (force) {
      this.creditsRequestByUserId.delete(userId);
    }
    const cached = this.creditsRequestByUserId.get(userId);
    if (cached && now - cached.at < 30 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/credits/clients/${userId}`).pipe(
      map((response: any) => {
        this._credits = response;
        this.updateCredistUser(response);
        return response;
      }),
      catchError(error => {
        this.creditsRequestByUserId.delete(userId);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.creditsRequestByUserId.set(userId, { at: now, req$ });
    return req$;
  }

  getCreditsMonthlyRanking(force = false, modalityId?: string | null): Observable<CreditsRankingResponse> {
    return this.getCreditsRankingByPeriod('monthly', force, modalityId);
  }

  getCreditsYearlyRanking(force = false, modalityId?: string | null): Observable<CreditsRankingResponse> {
    return this.getCreditsRankingByPeriod('yearly', force, modalityId);
  }

  private getCreditsRankingByPeriod(
    period: CreditsRankingPeriod,
    force = false,
    modalityId?: string | null,
  ): Observable<CreditsRankingResponse> {
    const now = Date.now();
    const gymId = this.gymService.getGym()?.id ?? '';
    const cacheKey = `${gymId}:${period}:${modalityId ?? 'all'}`;
    const cached = this.creditsRankingRequestByPeriod.get(cacheKey);
    if (!force && cached && now - cached.at < 15 * 1000) {
      return cached.req$;
    }

    let params = new HttpParams();
    if (modalityId) {
      params = params.set('modalityId', modalityId);
    }

    const req$ = this.http.get<CreditsRankingResponse>(
      `${this.baseUrl}/api/rankings/credits/${period}`,
      { params },
    ).pipe(
      map((response: CreditsRankingResponse) => response),
      catchError(error => {
        this.creditsRankingRequestByPeriod.delete(cacheKey);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );

    this.creditsRankingRequestByPeriod.set(cacheKey, { at: now, req$ });
    return req$;
  }

  getClassesToClient() {
    const user = this.getUser();
    if (!user?.id) {
      return of([]);
    }
    const now = Date.now();
    const cached = this.userClassesRequestByUserId.get(user.id);
    if (cached && now - cached.at < 30 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/booking/client/${user.id}`).pipe(
      map((response: any) => {
        this.updateUserClasses(response);
        return response;
      }),
      catchError(error => {
        this.userClassesRequestByUserId.delete(user.id);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.userClassesRequestByUserId.set(user.id, { at: now, req$ });
    return req$;
  }

  getHistoricClassesToClient() {
    const user = this.getUser();
    if (!user?.id) {
      return of([]);
    }
    const now = Date.now();
    const cached = this.userHistoricClassesRequestByUserId.get(user.id);
    if (cached && now - cached.at < 60 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/booking/client/historic/${user.id}`).pipe(
      map((response: any) => {
        this.updateHistoricUserClasses(response);
        return response;
      }),
      catchError(error => {
        this.userHistoricClassesRequestByUserId.delete(user.id);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.userHistoricClassesRequestByUserId.set(user.id, { at: now, req$ });
    return req$;
  }

  getEventBookingsToClient() {
    const user = this.getUser();
    if (!user?.id) {
      return of([]);
    }
    const now = Date.now();
    const cached = this.userEventBookingsRequestByUserId.get(user.id);
    if (cached && now - cached.at < 30 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/booking/events/client/${user.id}`).pipe(
      map((response: any) => (Array.isArray(response) ? response : [])),
      catchError(error => {
        this.userEventBookingsRequestByUserId.delete(user.id);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.userEventBookingsRequestByUserId.set(user.id, { at: now, req$ });
    return req$;
  }

  getEventBookings(eventId: string, page: number = 1, pageSize: number = 500) {
    if (!eventId) {
      return of([]);
    }
    return this.http
      .get(`${this.baseUrl}/api/events/${eventId}/bookings?page=${page}&pageSize=${pageSize}`)
      .pipe(
        map((response: any) => {
          if (Array.isArray(response)) {
            return response;
          }
          if (Array.isArray(response?.items)) {
            return response.items;
          }
          return [];
        }),
        catchError(error => {
          console.error(error);
          throw error;
        }),
      );
  }

  getBookingsByClassId(classId: string) {
    if (!classId) {
      return of([]);
    }
    const now = Date.now();
    const cached = this.bookingsByClassRequest.get(classId);
    if (cached && now - cached.at < 15 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/booking/class/${classId}`).pipe(
      map((response: any) => (Array.isArray(response) ? response : [])),
      catchError(error => {
        this.bookingsByClassRequest.delete(classId);
        console.error(error);
        return of([]);
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.bookingsByClassRequest.set(classId, { at: now, req$ });
    return req$;
  }

  // Obtener rutinas del cliente actual con caché local y tolerancia a 304
  getRoutinesToClient(): Observable<any[]> {
    const user = this.getUser();
    if (!user?.id) {
      return throwError(() => new Error('No client id available'));
    }

    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'If-Modified-Since': '0'
    });

    const url = `${this.baseUrl}/api/routine-assignments/client/${user.id}/routines?_=${Date.now()}`;

    return this.http.get(url, { observe: 'response', headers }).pipe(
      map((resp: any) => {
        const body = resp?.body;
        if (Array.isArray(body)) {
          this.routinesSubject.next(body);
          this.setLocalStorage(`routines:${user.id}`, body);
        } else {
          // Fallback en caso de 304 o respuesta sin cuerpo
          const cached = this.getLocalStorage(`routines:${user.id}`);
          if (Array.isArray(cached) && cached.length > 0) {
            this.routinesSubject.next(cached);
          }
        }
        return this.routinesSubject.value;
      }),
      catchError(error => {
        // En caso de error, devolver el último valor cacheado para no vaciar la UI
        console.error('Error fetching routines:', error);
        const cached = this.getLocalStorage(`routines:${user.id}`);
        if (Array.isArray(cached)) {
          this.routinesSubject.next(cached);
        }
        return of(this.routinesSubject.value);
      })
    );
  }

  getLocalStorage(_campo) {
    const campo = localStorage.getItem(_campo);
    if (campo) {
      return JSON.parse(campo);
    }
    return null;
  }
  setLocalStorage(campo, valor) {
    localStorage.setItem(campo, JSON.stringify(valor))
  }

  getToken(): string | null {
    const t = this.getLocalStorage('jwt');
    return typeof t === 'string' ? t : null;
  }

  isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1]));
      const exp = Number(payload?.exp) || 0;
      if (!exp) return true;
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  }

  logout(): void {
    try {
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
    } catch { }
    this.ngZone.run(() => this.userSubject.next(null));
    this.creditsSubject.next([]);
    this.creditsLoadedSubject.next(false);
  }

  /**
   * Invalida las cachés cuyo contenido depende del gimnasio activo. Se usa al
   * cambiar de gimnasio para que la app no muestre datos del gimnasio anterior.
   */
  resetGymScopedCache(): void {
    this.trainers = null;
    this.trainersGymId = '';
    this.trainersLoadedAt = 0;
    this.trainersRequest$ = undefined;
    this.availableBookingsLoadedAt = 0;
    this.availableBookingsRequest$ = undefined;
    this.allClassesLoadedAt = 0;
    this.allClassesRequest$ = undefined;
    this.nextClassesLoadedAt = 0;
    this.nextClassesRequest$ = undefined;
    this.userClassesRequestByUserId.clear();
    this.userHistoricClassesRequestByUserId.clear();
    this.classesByModalityRequest.clear();
    this.allClassesAndPassRequestByKey.clear();
    this.waitingListByModalityRequest.clear();
    this.bookingsByClassRequest.clear();
    this.creditsRankingRequestByPeriod.clear();
  }

  createClient(client, imagenFile?: File) {
    if (imagenFile) {
      const formData = new FormData();
      for (const key of Object.keys(client)) {
        if (key === 'avatar' || key === 'confirmPassword') { continue; }
        const value = client[key];
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      }
      formData.append('avatar', imagenFile, imagenFile.name);
      return handlePromise(
        firstValueFrom(
          this.http.post<any>(`${this.baseUrl}/api/clients/new`, formData)
        )
      );
    }

    const payload = { ...client };
    delete payload.confirmPassword;
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/clients/new`, payload)
      )
    );
  }

  subirImagen(imagenBase64: string, selectedFile: File) {
    // Datos del archivo para enviar
    const formData = new FormData();
    formData.append('file', selectedFile as any);
    formData.append('fileData', imagenBase64); // Puedes enviar base64 si lo prefieres.


    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/clients/newImage`, formData)
      )
    );
  }

  actualizarPerfil(usuarioData: any, imagenFile: File | null) {
    const formData = new FormData();
    const entries = Object.entries(usuarioData ?? {});

    for (const [key, rawValue] of entries) {
      if (key === 'avatar') {
        continue;
      }
      if (rawValue === undefined || rawValue === null) {
        continue;
      }

      if (typeof rawValue === 'string' && rawValue.trim().length === 0) {
        continue;
      }

      formData.append(key, String(rawValue));
    }

    // Si hay una imagen seleccionada, agregarla al formulario
    if (imagenFile) {
      formData.append('avatar', imagenFile, imagenFile.name);  // 'avatar' es el nombre del campo en el servidor
    }

    // Enviar los datos al servidor usando POST
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/clients/update-profile`, formData)
      )
    );
  }

  updateUser(user: any): void {
    const currentUser = this.getUser();
    const mergedUser =
      currentUser && user && typeof user === 'object'
        ? { ...currentUser, ...user }
        : user;

    if (mergedUser && typeof mergedUser === 'object') {
      const notifFlag =
        mergedUser.notification === true ||
        mergedUser.notifications === true ||
        (typeof mergedUser.notifications === 'boolean' ? mergedUser.notifications : false);
      mergedUser.notification = !!notifFlag;
      if (!Array.isArray(mergedUser.notifications)) {
        mergedUser.notifications = !!notifFlag;
      }
    }

    try {
      if (mergedUser) {
        this.setLocalStorage('user', mergedUser);
      }
    } catch {}

    this.ngZone.run(() => this.userSubject.next(mergedUser));
  }

  deleteAccountLogical() {
    const user = this.getUser();
    if (!user?.id) {
      return Promise.resolve({ data: null, error: new Error('No user authenticated') });
    }
    return handlePromise(
      firstValueFrom(
        this.http.delete<any>(`${this.baseUrl}/api/clients/${user.id}`)
      )
    );
  }

  // Método para obtener el usuario actual
  getUser() {
    return this.userSubject.value;
  }

  isAdmin() {
    const u = this.getUser() as any;
    if (!u) return false;
    const v = u?.isAdmin;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return ['true', '1', 'yes', 'si'].includes(String(v).toLowerCase());
    if (typeof v === 'number') return v !== 0;
    return false;
  }

  isTrainer() {
    const u = this.getUser() as any;
    if (!u) return false;
    const v = u?.isTrainer;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return ['true', '1', 'yes', 'si'].includes(String(v).toLowerCase());
    if (typeof v === 'number') return v !== 0;
    return false;
  }

  updateUserClasses(classes: any): void {

    this.userClassesSubject.next(classes);

  }

  updateHistoricUserClasses(classes: any): void {

    this.userHistoricClassesSubject.next(classes);

  }

  // Método para obtener el usuario actual
  getUserClasses() {
    return this.userClassesSubject.value;
  }

  updateUserMuscle(users: any): void {

    this.usersInMuscleSubject.next(users);

  }

  // Método para obtener el usuario actual
  getUserAccess() {
    return this.userAccessSubject.value;
  }

  updateUserAccess(entries: any): void {
    this.userAccessSubject.next(entries);

  }

  // Método para obtener el usuario actual
  getUserMuscle() {
    return this.usersInMuscleSubject.value;
  }

  updateUserInWaitingList(users: any): void {

    this.usersInWaitingListSubject.next(users);

  }

  // Método para obtener el usuario actual
  getUserInWaitingList() {
    return this.usersInWaitingListSubject.value;
  }

  getUsersInWaitingList(): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/waitinglist/all`).pipe(
      map(response => {
        this.updateUserInWaitingList(response);
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  getUsersInWaitingListByModalityId(modalityId: string): Observable<any> {
    const now = Date.now();
    const cached = this.waitingListByModalityRequest.get(modalityId);
    if (cached && now - cached.at < 15 * 1000) {
      return cached.req$;
    }
    const req$ = this.http.get(`${this.baseUrl}/api/waitinglist/modality/${modalityId}`).pipe(
      map(response => {
        this.updateUserInWaitingList(response);
        return response;
      }),
      catchError(error => {
        this.waitingListByModalityRequest.delete(modalityId);
        console.error(error);
        throw error;
      }),
      shareReplay({ bufferSize: 1, refCount: false, windowTime: 2 * 60 * 1000 })
    );
    this.waitingListByModalityRequest.set(modalityId, { at: now, req$ });
    return req$;
  }

  updateClasses(classes: any): void {
    this.classesSubject.next(classes);
  }

  updateEvents(classes: any): void {
    this.eventsSubject.next(classes);
  }

  updateOneClass(_class: any): void {
    const classId = _class?.clase?.id ?? _class?.class?.id ?? _class?.claseId ?? _class?.classId;
    if (!classId) return;
    const current = Array.isArray(this.getClasses()) ? [...this.getClasses()] : [];
    const idx = current.findIndex((c: any) => c?.id === classId);
    if (idx < 0) return;
    const cls: any = { ...current[idx] };
    const hasBookingsArray = Array.isArray(cls.bookings);
    if (hasBookingsArray) {
      cls.bookings = [...cls.bookings, _class];
      if (typeof cls.bookingsCount === 'number') {
        cls.bookingsCount = cls.bookingsCount + 1;
      } else {
        cls.bookingsCount = cls.bookings.length;
      }
    } else {
      const count = Number(cls.bookingsCount ?? 0) || 0;
      cls.bookingsCount = count + 1;
    }
    current[idx] = cls;
    this.classesSubject.next(current);
  }

  unregisterOneClass(_class: any): void {
    const current = Array.isArray(this.getClasses()) ? [...this.getClasses()] : [];
    const bookingId = _class?.id ?? _class?.bookingId;
    const classId = _class?.clase?.id ?? _class?.class?.id ?? _class?.claseId ?? _class?.classId;

    const idx = current.findIndex((c: any) => c?.id === classId || (Array.isArray(c?.bookings) && c.bookings.some((b: any) => String(b?.id) === String(bookingId))));
    if (idx < 0) return;
    const cls: any = { ...current[idx] };
    if (Array.isArray(cls.bookings) && bookingId != null) {
      cls.bookings = cls.bookings.filter((b: any) => String(b?.id) !== String(bookingId));
    }
    if (typeof cls.bookingsCount === 'number') {
      cls.bookingsCount = Math.max(0, cls.bookingsCount - 1);
    }
    current[idx] = cls;
    this.classesSubject.next(current);
  }

  getClasses() {
    return this.classesSubject.value;
  }

  getEvents() {
    return this.eventsSubject.value;
  }

  getAllCourses(): Observable<any[]> {
    return this.classesSubject.asObservable().pipe(filter(c => c !== null));
  }

  getAllClasses() {
    return this.classesAndPassSubject.value;
  }

  getCreditsUser() {
    return this.creditsSubject.value;
  }

  updateCredistUser(credits) {
    this.creditsSubject.next(credits);
    this.creditsLoadedSubject.next(true);
  }

  private _actualCreditsCache: { key: string; value: any } | null = null;
  private _actualCreditsOnlyMuscleCache: { key: string; value: any } | null = null;

  private actualCreditsKey(suffix: string): string {
    const list = this.creditsSubject.value;
    if (!Array.isArray(list) || list.length === 0) return `0::${suffix}`;
    let hash = `${list.length}`;
    for (let i = 0; i < Math.min(list.length, 5); i++) {
      const c = list[i];
      hash += `|${c?.id || ''}${c?.amount ?? 0}${c?.spenders ?? 0}${c?.isOnlyMuscle ? 'M' : 'N'}${c?.expirationDate || ''}`;
    }
    const last = list[list.length - 1];
    hash += `|L:${last?.id || ''}`;
    return `${hash}::${suffix}::${this.getTodayDate()}`;
  }

  getActualCreditsOnlyMuscle() {
    const key = this.actualCreditsKey('muscle');
    if (this._actualCreditsOnlyMuscleCache?.key === key) {
      return this._actualCreditsOnlyMuscleCache.value;
    }
    const today = this.getTodayDate();
    const c = this.creditsSubject.value.find((cr: any) => cr?.expirationDate >= today && cr?.isOnlyMuscle);
    this._actualCreditsOnlyMuscleCache = { key, value: c ?? null };
    return this._actualCreditsOnlyMuscleCache.value;
  }

  getActualCredits() {
    const key = this.actualCreditsKey('normal');
    if (this._actualCreditsCache?.key === key) {
      return this._actualCreditsCache.value;
    }
    const today = this.getTodayDate();
    let c = this.creditsSubject.value.find(
      (cr: any) =>
        cr?.expirationDate >= today &&
        (Number(cr?.amount ?? 0) - Number(cr?.spenders ?? 0)) > 0 &&
        !cr?.isOnlyMuscle,
    );
    if (!c) {
      c = this.creditsSubject.value.find(
        (cr: any) =>
          cr?.expirationDate >= today &&
          (Number(cr?.amount ?? 0) - Number(cr?.spenders ?? 0)) >= 0 &&
          !cr?.isOnlyMuscle,
      );
    }
    this._actualCreditsCache = { key, value: c ?? null };
    return this._actualCreditsCache.value;
  }

  updateAllClasses(classes: any): void {
    this.classesAndPassSubject.next(classes);
  }

  private toDateOnly(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return String(value).split('T')[0];
  }

  /** Indica si el snapshot de la tarifa plana incluye la modalidad indicada. */
  private rateIncludesModality(clientRate: any, modalityId: string): boolean {
    const modalities = clientRate?.rateSnapshot?.modalities;
    return (
      Array.isArray(modalities) &&
      modalities.some((m: any) => String(m?.id ?? m) === String(modalityId))
    );
  }

  /** Indica si la tarifa plana usa el modo de presupuesto común ponderado. */
  private isSharedBudget(clientRate: any): boolean {
    return (
      clientRate?.sharedBudget === true ||
      clientRate?.rateSnapshot?.sharedBudget === true
    );
  }

  /** Unidades de la bolsa común que consume una reserva en la modalidad indicada. */
  private getUnitsPerBooking(clientRate: any, modalityId: string | null): number {
    if (!modalityId) return 1;
    const limits = Array.isArray(clientRate?.modalityLimits)
      ? clientRate.modalityLimits
      : Array.isArray(clientRate?.rateSnapshot?.modalityLimits)
        ? clientRate.rateSnapshot.modalityLimits
        : [];
    const limit = limits.find(
      (ml: any) => String(ml?.modalityId) === String(modalityId),
    );
    const factor = Number(limit?.unitsPerBooking);
    return Number.isFinite(factor) && factor > 0 ? factor : 1;
  }

  /**
   * Comprueba si a la tarifa plana le queda cupo en la VENTANA ACTUAL para la
   * modalidad indicada. El límite de reservas se aplica por ventana, no global:
   * se usa el consumo de la ventana (`modalityUsageInPeriod` / `usedBookingsInPeriod`)
   * en lugar de los contadores globales. Un límite 0 significa ilimitado.
   * En modo presupuesto común `maxBookings` es una bolsa de unidades ponderada
   * y el cupo por modalidad lo calcula el backend (`modalityAvailableInPeriod`).
   */
  private hasBookingsAvailableInCurrentWindow(
    clientRate: any,
    modalityId: string | null,
  ): boolean {
    if (this.isSharedBudget(clientRate)) {
      const budget = Number(clientRate?.maxBookings ?? 0) || 0;
      if (budget === 0) return true;
      const available = modalityId
        ? clientRate?.modalityAvailableInPeriod?.[modalityId]
        : undefined;
      if (typeof available === 'number') return available > 0;
      const remaining =
        Number(clientRate?.sharedBudgetRemainingUnits ?? budget) || 0;
      return remaining >= this.getUnitsPerBooking(clientRate, modalityId);
    }

    const limits = Array.isArray(clientRate?.modalityLimits)
      ? clientRate.modalityLimits
      : [];
    const specificLimit = modalityId
      ? limits.find((ml: any) => String(ml?.modalityId) === String(modalityId))
      : null;
    const effectiveMax = specificLimit
      ? Number(specificLimit?.maxBookings ?? 0) || 0
      : Number(clientRate?.maxBookings ?? 0) || 0;
    if (effectiveMax === 0) return true;

    const usedInWindow = specificLimit
      ? Number(clientRate?.modalityUsageInPeriod?.[modalityId as string] ?? 0) || 0
      : Number(clientRate?.usedBookingsInPeriod ?? 0) || 0;
    return usedInWindow < effectiveMax;
  }

  getAvailableBookings(clientId: string, force = false): Observable<any> {
    if (!clientId) return of(null);
    const CACHE_TTL = 30 * 1000;
    const now = Date.now();
    if (
      !force &&
      this.availableBookingsRequest$ &&
      now - this.availableBookingsLoadedAt < CACHE_TTL
    ) {
      return this.availableBookingsRequest$;
    }

    const url = `${this.baseUrl}/api/clients/${clientId}/available-bookings`;
    const headers = new HttpHeaders().set('x-client-id', clientId);
    const req$ = this.http.get<any>(url, { headers }).pipe(
      map((data) => {
        this.availableBookingsSubject.next(data ?? null);
        this.availableBookingsLoadedAt = Date.now();
        this.availableBookingsRequest$ = undefined;
        return data ?? null;
      }),
      catchError((error) => {
        this.availableBookingsRequest$ = undefined;
        console.error('Error getAvailableBookings:', error);
        return of(null);
      }),
      shareReplay(1),
    );
    this.availableBookingsRequest$ = req$;
    return req$;
  }

  getAvailableBookingsSnapshot() {
    return this.availableBookingsSubject.getValue();
  }

  getClientRateHistory(clientId: string): Observable<any[]> {
    if (!clientId) return of([]);
    const headers = new HttpHeaders().set('x-client-id', clientId);
    return this.http
      .get<any[]>(`${this.baseUrl}/api/client-rates/my/${clientId}`, { headers })
      .pipe(
        map((data) => (Array.isArray(data) ? data : [])),
        catchError((error) => {
          console.error('Error getClientRateHistory:', error);
          return of([]);
        }),
      );
  }

  createPasswordCode(email) {
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/clients/recovery-password`, { email })
      )
    );
  }

  newPassword(client) {
    return handlePromise(
      firstValueFrom(
        this.http.put<any>(`${this.baseUrl}/api/clients/new-password`, client)
      )
    );
  }

  saveToken(client, token) {
    try {
      if (client && token) {
        return handlePromise(
          firstValueFrom(
            this.http.post<any>(`${this.baseUrl}/api/notifications/save-token`, { token, client })
          )
        );
      }
    } catch (e) {
      console.log(e);
    }
  }

  getAllNotifications() {
    const user = this.getUser();
    return this.http.get(`${this.baseUrl}/api/notifications/all/${user.id}`).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  getUserServerData(id: string) {
    return this.http.get(`${this.baseUrl}/api/auth-client/${id}`).pipe(
      map((response: any) => response),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  markNotificationReaded() {
    const user = this.getUser();
    return handlePromise(
      firstValueFrom(
        this.http.put<any>(`${this.baseUrl}/api/notifications/readed`, { client: user.id })
      )
    );
  }

  createSugestion(message: string) {
    const user = this.getUser();
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/clients/new-sugestion`, { email: user.email, dni: user.personalId, comment: message })
      )
    );
  }

  // Community methods (módulo Community backend @Controller('community'))
  // Endpoints: GET/POST /api/community/posts, DELETE posts/:id, POST like/comments, POST upload
  getPosts(page: number = 1, limit: number = 20): Observable<{ items: any[]; totalItems: number }> {
    return this.http.get<{ items: any[]; totalItems: number }>(
      `${this.baseUrl}/api/community/posts?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`
    ).pipe(
      map((res) => ({
        items: res?.items ?? [],
        totalItems: typeof res?.totalItems === 'number' ? res.totalItems : (res?.items?.length ?? 0),
      })),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  createPost(content: string, images?: string[]): Observable<any> {
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.post<any>(
      `${this.baseUrl}/api/community/posts`,
      { content, images: images && images.length ? images : undefined },
      { headers }
    ).pipe(
      map((r) => r),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  deletePost(postId: string): Observable<any> {
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.delete<any>(`${this.baseUrl}/api/community/posts/${encodeURIComponent(postId)}`, { headers }).pipe(
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  toggleLike(postId: string): Observable<{ liked: boolean; likesCount: number }> {
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.post<{ liked: boolean; likesCount: number }>(
      `${this.baseUrl}/api/community/posts/${encodeURIComponent(postId)}/like`,
      {},
      { headers }
    ).pipe(catchError(error => { console.error(error); throw error; }));
  }

  addComment(postId: string, commentText: string): Observable<any> {
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.post<any>(
      `${this.baseUrl}/api/community/posts/${encodeURIComponent(postId)}/comments`,
      { content: commentText },
      { headers }
    ).pipe(catchError(error => { console.error(error); throw error; }));
  }

  deleteComment(commentId: string): Observable<any> {
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.delete<any>(
      `${this.baseUrl}/api/community/comments/${encodeURIComponent(commentId)}`,
      { headers }
    ).pipe(catchError(error => { console.error(error); throw error; }));
  }

  uploadPostImage(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('photo', file, file.name || `photo-${Date.now()}`);
    const user = this.getUser();
    const clientId = (user as any)?.id ?? '';
    const headers = new HttpHeaders().set('x-client-id', String(clientId || ''));
    return this.http.post<{ url: string }>(
      `${this.baseUrl}/api/community/upload`,
      formData,
      {
        headers,
        reportProgress: false,
      }
    ).pipe(
      map((r) => ({ url: r?.url ?? '' })),
      catchError(error => {
        console.error(error);
        throw error;
      })
    );
  }

  createQr() {
    const user = this.getUser();
    const client = {
      email: user.email,
      personalId: user.personalId,
      clientId: user.clientId,
    }
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/auth-client/qr`, client)
      )
    );
  }

  clearQr() {
    const user = this.getUser();
    if (!user) return Promise.resolve({ data: { ok: true }, error: null });
    const client = {
      email: user.email,
      personalId: user.personalId,
      clientId: user.clientId,
    };
    const mergedUser = { ...user, qrCode: null };
    this.updateUser(mergedUser);
    return handlePromise(
      firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/api/auth-client/qr/clear`, client)
      )
    );
  }

  get classes() {
    return this._classes;
  }

  set classes(classes) {
    this._classes = classes;
  }

  get allClasses() {
    return this._allClasses;
  }

  set allClasses(classes) {
    this._allClasses = classes;
  }

  get allClassesAndPass() {
    return this._allClassesAndPass;
  }

  set allClassesAndPass(classes) {
    this._allClassesAndPass = classes;
  }

  private getTodayDate() {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm: any = today.getMonth() + 1; // Los meses empiezan desde 0
    let dd: any = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    return `${yyyy}-${mm}-${dd}`;
  }

  get credits() {
    return this._credits.find(c => c.expirationDate >= this.getTodayDate() && (c.amount - c.spenders) > 0);
  }

  set credits(credits) {
    this._credits = credits;
  }

  get courses() {
    return this._courses;
  }

  set courses(courses) {
    this._courses = courses;
  }

  get events() {
    return this._events;
  }

  set events(events) {
    this._events = events;
  }

  get newCourses() {
    return this._newCourses;
  }

  set newCourses(courses) {
    this._newCourses = courses;
  }

  get trainers() {
    return this._trainers;
  }

  set trainers(courses) {
    this._trainers = courses;
  }

  updateClassDetailInCaches(classId: string, patch: Record<string, any>): void {
    if (!classId) return;
    this.updateClassesDetailInCaches([classId], patch);
  }

  updateClassesDetailInCaches(classIds: string[], patch: Record<string, any>): void {
    if (!Array.isArray(classIds) || classIds.length === 0 || !patch) return;
    const ids = new Set(classIds.map((id) => String(id)));
    if (ids.size === 0) return;

    const classesArr = Array.isArray(this.getClasses()) ? [...this.getClasses()] : [];
    let mutatedClasses = false;
    for (let i = 0; i < classesArr.length; i++) {
      const c = classesArr[i] ?? {};
      if (ids.has(String(c?.id ?? ''))) {
        classesArr[i] = { ...c, ...patch };
        mutatedClasses = true;
      }
    }
    if (mutatedClasses) {
      this.classesSubject.next(classesArr);
    }

    const allAndPass = Array.isArray(this._allClassesAndPass) ? [...this._allClassesAndPass] : [];
    let mutated = false;
    for (let i = 0; i < allAndPass.length; i++) {
      const c = allAndPass[i] ?? {};
      if (ids.has(String(c?.id ?? ''))) {
        allAndPass[i] = { ...c, ...patch };
        mutated = true;
      }
    }
    if (mutated) {
      this._allClassesAndPass = allAndPass;
      this.classesAndPassSubject.next(allAndPass);
    }

    const evts = Array.isArray(this._events) ? [...this._events] : [];
    let mutatedEvt = false;
    for (let i = 0; i < evts.length; i++) {
      const c = evts[i] ?? {};
      if (ids.has(String(c?.id ?? ''))) {
        evts[i] = { ...c, ...patch };
        mutatedEvt = true;
      }
    }
    if (mutatedEvt) {
      this._events = evts;
      this.eventsSubject.next(evts);
    }
  }

  private resolveAffectedClassIds(res: any, classId: string): string[] {
    const ids = res?.data?.affectedClassIds;
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map((id: any) => String(id));
    }
    return [classId];
  }

  uploadClassImage(classId: string, file: File, onlyThisClass = false) {
    if (!classId || !file) {
      return Promise.resolve({ data: null, error: new Error('Faltan datos') });
    }
    const formData = new FormData();
    formData.append('image', file, file.name);
    if (onlyThisClass) {
      formData.append('onlyThisClass', 'true');
    }
    return handlePromise(
      firstValueFrom(
        this.http.patch<any>(`${this.baseUrl}/api/classes/${classId}/image`, formData)
      )
    ).then((res: any) => {
      const image = res?.data?.image ?? null;
      if (image) {
        this.updateClassesDetailInCaches(this.resolveAffectedClassIds(res, classId), { image });
      }
      // La imagen es común a todas las clases de la modalidad ese día (salvo que
      // se marque "solo esta clase"): invalida la caché por modalidad para que
      // el cambio se refleje al volver.
      this.classesByModalityRequest.clear();
      return res;
    });
  }

  deleteClassImage(classId: string, onlyThisClass = false) {
    if (!classId) {
      return Promise.resolve({ data: null, error: new Error('Falta classId') });
    }
    const url = `${this.baseUrl}/api/classes/${classId}/image${
      onlyThisClass ? '?onlyThisClass=true' : ''
    }`;
    return handlePromise(
      firstValueFrom(this.http.delete<any>(url))
    ).then((res: any) => {
      this.updateClassesDetailInCaches(this.resolveAffectedClassIds(res, classId), { image: null });
      this.classesByModalityRequest.clear();
      return res;
    });
  }


}
